import { FC, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { Form, SubmitButton, useForm } from "capitalrx-components-2.0/lib/forms";
import { Field as TextField } from "capitalrx-components-2.0/lib/forms/Textbox/Field";
import { Button, Message, Messages } from "capitalrx-components-2.0/lib/general";
import { Stack, Shelf, useResponsive, Grid } from "capitalrx-components-2.0/lib/layout";
import { Body, Title } from "capitalrx-components-2.0/lib/typography";

import { MemberSearchErrors } from "@/constants";
import { ThemeSelectorContext } from "@/context";
import { ANALYTIC_EVENTS, logEvent } from "@/service/analyticsService";
import { formatDateWithSlashes, getThemeURL, getThemeButton, isDate, currentEnvironment } from "@/utils";

import styles from "./PatientLookupForm.module.scss";

export interface PatientForm {
  readonly provider_npi: string;
  readonly external_member_id: string;
  readonly member_date_of_birth: string;
  readonly member_first: string;
  readonly member_last: string;
}


const RESPONSIVE_LAYOUT = {
  Layout: {
    desktop: Grid,
    mobile: Stack,
  },
};

export const PatientLookupForm: FC = () => {
  const navigate = useNavigate();
  const { Layout } = useResponsive(RESPONSIVE_LAYOUT);
  const { primary_color } = useContext(ThemeSelectorContext);

  const form = useForm<PatientForm>();
  // Error if the call to the member search errors out
  const [error, setError] = useState<string>();

  const { setValue } = form;
  const [originalMemberId, setOriginalMemberId] = useState<string>("");
  const [originalDOB, setOriginalDOB] = useState<string>("");

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const error = searchParams.get("error");
    if (error === MemberSearchErrors.NOT_FOUND) {
      setError("No patient found - check that all information is correct");
    }

    const fields: (keyof PatientForm)[] = [
      "provider_npi",
      "member_first",
      "member_last",
      "external_member_id",
      "member_date_of_birth",
    ];

    fields.forEach((field) => {
      const value = searchParams.get(field);
      if (value) {
        if (field === "external_member_id") {
          setOriginalMemberId(value);
          const blurred = value.replace(/[0-9a-zA-Z]/g, "*");
          setValue(field, blurred);
        } else if (field === "member_date_of_birth") {
          setOriginalDOB(value);
          const blurredDOB = value.replace(/[0-9]/g, "*");
          setValue(field, blurredDOB);
        } else {
          setValue(field, value);
        }
      }
    });
    setSearchParams({});
  }, [searchParams, setValue]);


  const handleSearch = useCallback(
    async (data: PatientForm) => {

      // analytics log search clicked
      logEvent(ANALYTIC_EVENTS.MEDICAL.memberSearch);

      // Validate inputs
      const dateError = isDate(originalDOB);
      if (originalDOB && dateError !== true) {
        setError(dateError as string);
        return;
      }

      const params = {
        provider_npi: data.provider_npi,
        external_member_id: originalMemberId,
        member_date_of_birth: originalDOB,
        member_first: data.member_first,
        member_last: data.member_last,
      };
      const encodedParams = new URLSearchParams();
      for (const key in params) {
        if (params[key]) {
          encodedParams.append(key, params[key]);
        }
      }
      navigate((`${getThemeURL("PATIENT_COVERAGE_SEARCH")}?${encodedParams.toString()}`));

    },
    [navigate, originalMemberId, originalDOB],
  );

  const resetForm = () => {
    form.reset();
  };

  const handleMemberIdFocus = useCallback(() => {
    setValue("external_member_id", originalMemberId);
  }, [originalMemberId, setValue]);

  const handleMemberIdBlur = useCallback(() => {
    const memberId = form.getValues("external_member_id");
    setOriginalMemberId(memberId);
    const replaceVal = memberId.replace(new RegExp("[0-9a-zA-Z]", "g"), "*");
    setValue("external_member_id", replaceVal);
  }, [setValue, form]);

  const handleDOBFocus = useCallback(() => {
    setValue("member_date_of_birth", originalDOB);
  }, [originalDOB, setValue]);

  const handleDOBBlur = useCallback(() => {
    const memberDOB = form.getValues("member_date_of_birth");
    setOriginalDOB(memberDOB);
    const replaceDOBVal = memberDOB.replace(new RegExp("[0-9]", "g"), "*");
    setValue("member_date_of_birth", replaceDOBVal);
  }, [setValue, form]);


  const handleChange = useCallback(() => {
    const memberDOB = form.getValues("member_date_of_birth");
    setOriginalDOB(memberDOB);
    const replaceDOBVal = formatDateWithSlashes(memberDOB);
    setValue("member_date_of_birth", replaceDOBVal);
  }, [setValue, form]);

  const fillDemoDataClick = useCallback(() => {
    if (currentEnvironment === "development") {
      form.setValue("provider_npi", "9990000001");
      form.setValue("member_first", "Marco");
      form.setValue("member_last", "Rambo");

      form.setValue("external_member_id", "99880002");
      form.setValue("member_date_of_birth", "03/22/1975");

    }
    if (currentEnvironment === "staging") {
      form.setValue("provider_npi", "1437269552");
      form.setValue("member_first", "Benjamin");
      form.setValue("member_last", "Franklin");

      form.setValue("external_member_id", "74832345");
      form.setValue("member_date_of_birth", "04/01/1976");
    }
    handleMemberIdBlur();
    handleDOBBlur();
  }, [form, handleMemberIdBlur, handleDOBBlur]);

  return (
    <div className={styles.MemberSearchContainer}>
      <Stack spacing="medium-large">
        <Stack spacing="small">
          <Title testId="test__patient_lookup_heading">Patient Tools</Title>
          <Body testId="test__patient_lookup_description">Find medical service coverage, accumulations, drug coverage, and prior authorization cases</Body>
        </Stack>
        <Form form={form} onSubmit={handleSearch}>
          <Stack>
            <TextField required label="Provider NPI" name={"provider_npi"} testId="test__provider_npi" />
            <Layout>
              <TextField required label="Patient First Name" name={"member_first"} testId="test__member_first" />
              <TextField required label="Patient Last Name" name={"member_last"} testId="test__member_last" />
            </Layout>
            <Layout>
              <div onFocus={handleMemberIdFocus} onBlur={handleMemberIdBlur} >
                <TextField required label="Member ID" name={"external_member_id"} className={styles.textFieldWrapper} testId="test__external_member_id" />
              </div>
              <div onFocus={handleDOBFocus} onBlur={handleDOBBlur} onChange={handleChange}>
                <TextField
                  instructions="mm/dd/yyyy"
                  required
                  label="Patient Date of Birth"
                  name={"member_date_of_birth"}
                  placeholder="__/__/____"
                  className={styles.textFieldWrapper}
                  testId="test__member_date_of_birth"
                />
              </div>
            </Layout>
            {/* Set error if the call fails */}
            {error && <Message appearance={Messages.Error} visible>{error}</Message>}
            <Shelf spacing="small">
              <SubmitButton appearance={getThemeButton("primary", primary_color)} testId="test__view_patient_submit">View Patient</SubmitButton>
              <Button
                appearance={getThemeButton("secondary", primary_color)}
                onClick={resetForm}
                testId="test__reset_form"
              >Clear</Button>
              <Shelf.Spacer />
              {["development", "staging"].includes(currentEnvironment) && (
                <Button onClick={fillDemoDataClick} appearance={getThemeButton("secondary", primary_color)}>Fill Demo Data</Button>
              )}
              </Shelf>
          </Stack>
        </Form>
      </Stack >
    </div >
  );
};

export default PatientLookupForm;
